/**
 * Data layer for `react-native-sdk`. Thin typed fetch wrappers over /api/v1/react-native-sdk.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface ReactNativeSdkRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListReactNativeSdkResponse {
	count: number;
	items: ReactNativeSdkRow[];
}

export interface CreateReactNativeSdkBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/react-native-sdk";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListReactNativeSdkResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListReactNativeSdkResponse>(res);
}

export async function create(body: CreateReactNativeSdkBody): Promise<ReactNativeSdkRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<ReactNativeSdkRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const reactNativeSdkQueryKeys = {
	all: ["react-native-sdk"] as const,
	list: () => [...reactNativeSdkQueryKeys.all] as const,
	detail: (id: string) => [...reactNativeSdkQueryKeys.all, id] as const,
};
